
import { DotNetModule } from '../types';

export const DOTNET_ADVANCED_MODULES: DotNetModule[] = [
  {
    id: "dotnet-microservices-architecture",
    title: "Microservices Architecture with .NET",
    description: "Design and implement microservices-based applications using .NET technologies.",
    content: `
# Microservices Architecture with .NET

Learn to design, implement, and deploy microservices using .NET technologies.

## Understanding Microservices

Microservices architecture breaks down applications into small, independent services that communicate over well-defined APIs.

### Key Principles
- Single Responsibility
- Decentralized
- Fault Tolerant
- Technology Agnostic

## .NET Technologies for Microservices

### ASP.NET Core Web APIs
\`\`\`csharp
[ApiController]
[Route("api/[controller]")]
public class ProductController : ControllerBase
{
    private readonly IProductService _productService;
    
    public ProductController(IProductService productService)
    {
        _productService = productService;
    }
    
    [HttpGet]
    public async Task<ActionResult<IEnumerable<Product>>> GetProducts()
    {
        var products = await _productService.GetAllAsync();
        return Ok(products);
    }
}
\`\`\`

### Service Communication
- HTTP/REST APIs
- gRPC
- Message Queues (RabbitMQ, Azure Service Bus)

## Docker and Containerization

\`\`\`dockerfile
FROM mcr.microsoft.com/dotnet/aspnet:8.0 AS base
WORKDIR /app
EXPOSE 80

FROM mcr.microsoft.com/dotnet/sdk:8.0 AS build
WORKDIR /src
COPY ["ProductService/ProductService.csproj", "ProductService/"]
RUN dotnet restore "ProductService/ProductService.csproj"
COPY . .
WORKDIR "/src/ProductService"
RUN dotnet build "ProductService.csproj" -c Release -o /app/build
\`\`\`

## Best Practices
- API Versioning
- Circuit Breaker Pattern
- Health Checks
- Distributed Tracing
`,
    duration: "3 hours",
    difficulty: "advanced",
    objectives: [
      "Understand microservices architecture principles",
      "Build ASP.NET Core Web APIs",
      "Implement service communication patterns",
      "Deploy services using Docker containers",
      "Apply microservices best practices"
    ],
    order: 1
  },
  {
    id: "dotnet-testing-strategies",
    title: "Comprehensive Testing Strategies",
    description: "Master unit testing, integration testing, and end-to-end testing in .NET applications.",
    content: `
# Comprehensive Testing Strategies

Implement robust testing strategies for enterprise .NET applications.

## Testing Pyramid

### Unit Tests
Test individual components in isolation.

\`\`\`csharp
[Test]
public void CalculateTotal_ShouldReturnCorrectSum()
{
    // Arrange
    var calculator = new PriceCalculator();
    var items = new List<Item>
    {
        new Item { Price = 10.50m },
        new Item { Price = 25.00m }
    };
    
    // Act
    var result = calculator.CalculateTotal(items);
    
    // Assert
    Assert.That(result, Is.EqualTo(35.50m));
}
\`\`\`

### Integration Tests
Test component interactions and external dependencies.

\`\`\`csharp
[Test]
public async Task CreateProduct_ShouldSaveToDatabase()
{
    // Arrange
    var factory = new WebApplicationFactory<Program>();
    var client = factory.CreateClient();
    var product = new CreateProductRequest
    {
        Name = "Test Product",
        Price = 99.99m
    };
    
    // Act
    var response = await client.PostAsJsonAsync("/api/products", product);
    
    // Assert
    Assert.That(response.StatusCode, Is.EqualTo(HttpStatusCode.Created));
}
\`\`\`

## Testing Frameworks
- NUnit / xUnit
- Moq for mocking
- FluentAssertions
- TestContainers for integration tests

## Test-Driven Development (TDD)
1. Write failing test
2. Write minimal code to pass
3. Refactor
4. Repeat
`,
    duration: "2.5 hours",
    difficulty: "advanced",
    objectives: [
      "Implement comprehensive testing strategies",
      "Write effective unit and integration tests",
      "Apply Test-Driven Development practices",
      "Use mocking frameworks effectively",
      "Set up automated testing pipelines"
    ],
    order: 2
  },
  {
    id: "dotnet-signalr-realtime",
    title: "Real-time Applications with SignalR",
    description: "Build real-time, interactive web applications using ASP.NET Core SignalR.",
    content: `
# Real-time Applications with SignalR

Create interactive, real-time web applications using ASP.NET Core SignalR.

## SignalR Overview

SignalR enables real-time web functionality by allowing server-side code to push content to clients instantly.

### Key Features
- Automatic connection management
- Multiple transport protocols (WebSockets, Server-Sent Events, Long Polling)
- Automatic fallback to best supported transport
- Built-in scaling support

## Setting up SignalR

### Server Configuration
\`\`\`csharp
// Program.cs
builder.Services.AddSignalR();

app.MapHub<ChatHub>("/chatHub");
\`\`\`

### Creating a Hub
\`\`\`csharp
public class ChatHub : Hub
{
    public async Task SendMessage(string user, string message)
    {
        await Clients.All.SendAsync("ReceiveMessage", user, message);
    }
    
    public async Task JoinGroup(string groupName)
    {
        await Groups.AddToGroupAsync(Context.ConnectionId, groupName);
        await Clients.Group(groupName).SendAsync("UserJoined", Context.User.Identity.Name);
    }
}
\`\`\`

### Client-side JavaScript
\`\`\`javascript
const connection = new signalR.HubConnectionBuilder()
    .withUrl("/chatHub")
    .build();

connection.start().then(function () {
    console.log("SignalR connected");
}).catch(function (err) {
    console.error(err.toString());
});

connection.on("ReceiveMessage", function (user, message) {
    const li = document.createElement("li");
    li.textContent = \`\${user}: \${message}\`;
    document.getElementById("messagesList").appendChild(li);
});
\`\`\`

## Real-time Scenarios
- Chat applications
- Live dashboards
- Collaborative editing
- Gaming applications
- Live notifications
`,
    duration: "2 hours",
    difficulty: "advanced",
    objectives: [
      "Understand SignalR architecture and protocols",
      "Implement real-time hubs and connections",
      "Handle client-server communication",
      "Build scalable real-time applications",
      "Implement group-based messaging"
    ],
    order: 3
  },
  {
    id: "dotnet-performance-optimization",
    title: "Performance Optimization and Monitoring",
    description: "Optimize application performance and implement comprehensive monitoring solutions.",
    content: `
# Performance Optimization and Monitoring

Master techniques for optimizing .NET application performance and implementing monitoring.

## Performance Profiling

### Memory Management
\`\`\`csharp
// Efficient string operations
var sb = new StringBuilder();
foreach (var item in items)
{
    sb.AppendLine(item.ToString());
}
string result = sb.ToString();

// Span<T> for high-performance scenarios
ReadOnlySpan<char> span = text.AsSpan();
var part = span.Slice(start, length);
\`\`\`

### Async/Await Best Practices
\`\`\`csharp
// Use ConfigureAwait(false) in library code
public async Task<string> GetDataAsync()
{
    var response = await httpClient.GetStringAsync(url).ConfigureAwait(false);
    return ProcessData(response);
}

// Avoid async void except for event handlers
public async void Button_Click(object sender, EventArgs e)
{
    try
    {
        await ProcessClickAsync();
    }
    catch (Exception ex)
    {
        // Handle exception
    }
}
\`\`\`

## Monitoring and Observability

### Application Insights
\`\`\`csharp
builder.Services.AddApplicationInsightsTelemetry();

// Custom telemetry
public class ProductService
{
    private readonly TelemetryClient _telemetryClient;
    
    public ProductService(TelemetryClient telemetryClient)
    {
        _telemetryClient = telemetryClient;
    }
    
    public async Task<Product> GetProductAsync(int id)
    {
        using var activity = _telemetryClient.StartOperation<DependencyTelemetry>("GetProduct");
        
        var stopwatch = Stopwatch.StartNew();
        var product = await _repository.GetByIdAsync(id);
        stopwatch.Stop();
        
        _telemetryClient.TrackMetric("ProductLoadTime", stopwatch.ElapsedMilliseconds);
        return product;
    }
}
\`\`\`

### Health Checks
\`\`\`csharp
builder.Services.AddHealthChecks()
    .AddDbContext<ApplicationDbContext>()
    .AddRedis(connectionString)
    .AddUrlGroup(new Uri("https://api.external.com/health"), "External API");

app.MapHealthChecks("/health");
\`\`\`

## Caching Strategies
- In-Memory Caching
- Distributed Caching (Redis)
- Response Caching
- Output Caching
`,
    duration: "3 hours",
    difficulty: "advanced",
    objectives: [
      "Identify and resolve performance bottlenecks",
      "Implement effective caching strategies",
      "Set up comprehensive monitoring solutions",
      "Optimize memory usage and garbage collection",
      "Use profiling tools effectively"
    ],
    order: 4
  },
  {
    id: "dotnet-cicd-deployment",
    title: "CI/CD Pipelines and Production Deployment",
    description: "Implement continuous integration and deployment pipelines for .NET applications.",
    content: `
# CI/CD Pipelines and Production Deployment

Set up robust CI/CD pipelines and deploy .NET applications to production environments.

## Azure DevOps Pipelines

### Build Pipeline (azure-pipelines.yml)
\`\`\`yaml
trigger:
- main

pool:
  vmImage: 'ubuntu-latest'

variables:
  buildConfiguration: 'Release'

steps:
- task: UseDotNet@2
  inputs:
    packageType: 'sdk'
    version: '8.x'

- task: DotNetCoreCLI@2
  displayName: 'Restore packages'
  inputs:
    command: 'restore'
    projects: '**/*.csproj'

- task: DotNetCoreCLI@2
  displayName: 'Build'
  inputs:
    command: 'build'
    projects: '**/*.csproj'
    arguments: '--configuration $(buildConfiguration)'

- task: DotNetCoreCLI@2
  displayName: 'Run tests'
  inputs:
    command: 'test'
    projects: '**/*Tests.csproj'
    arguments: '--configuration $(buildConfiguration) --collect "Code coverage"'

- task: DotNetCoreCLI@2
  displayName: 'Publish'
  inputs:
    command: 'publish'
    publishWebProjects: true
    arguments: '--configuration $(buildConfiguration) --output $(Build.ArtifactStagingDirectory)'

- task: PublishBuildArtifacts@1
  inputs:
    PathtoPublish: '$(Build.ArtifactStagingDirectory)'
    ArtifactName: 'drop'
\`\`\`

## GitHub Actions

### .NET Workflow
\`\`\`yaml
name: .NET CI/CD

on:
  push:
    branches: [ main ]
  pull_request:
    branches: [ main ]

jobs:
  build-and-test:
    runs-on: ubuntu-latest
    
    steps:
    - uses: actions/checkout@v3
    
    - name: Setup .NET
      uses: actions/setup-dotnet@v3
      with:
        dotnet-version: '8.x'
    
    - name: Restore dependencies
      run: dotnet restore
    
    - name: Build
      run: dotnet build --no-restore
    
    - name: Test
      run: dotnet test --no-build --verbosity normal
    
    - name: Publish
      run: dotnet publish -c Release -o ./publish
    
    - name: Deploy to Azure Web App
      uses: azure/webapps-deploy@v2
      with:
        app-name: 'myapp'
        publish-profile: \${{ secrets.AZURE_WEBAPP_PUBLISH_PROFILE }}
        package: './publish'
\`\`\`

## Docker Deployment

### Multi-stage Dockerfile
\`\`\`dockerfile
FROM mcr.microsoft.com/dotnet/sdk:8.0 AS build
WORKDIR /src
COPY ["MyApp/MyApp.csproj", "MyApp/"]
RUN dotnet restore "MyApp/MyApp.csproj"
COPY . .
WORKDIR "/src/MyApp"
RUN dotnet build "MyApp.csproj" -c Release -o /app/build

FROM build AS publish
RUN dotnet publish "MyApp.csproj" -c Release -o /app/publish

FROM mcr.microsoft.com/dotnet/aspnet:8.0 AS final
WORKDIR /app
COPY --from=publish /app/publish .
ENTRYPOINT ["dotnet", "MyApp.dll"]
\`\`\`

## Production Considerations
- Environment configuration
- Secrets management
- Load balancing
- Database migrations
- Monitoring and logging
`,
    duration: "2.5 hours",
    difficulty: "advanced",
    objectives: [
      "Set up CI/CD pipelines for .NET applications",
      "Deploy applications to cloud platforms",
      "Implement automated testing in pipelines",
      "Manage application secrets and configuration",
      "Monitor production deployments"
    ],
    order: 5
  }
];
